import { APEX_API_URL } from '@/constants';
import { isServerSide } from '@/helpers';
import { INextAuthSessionModel } from '@/interfaces/authentication';
import { BannerPayLoad } from '@/interfaces/banner';
import { ICategory } from '@/interfaces/category';
import { IBrandCustomFilters, ICustomFilter } from '@/interfaces/customFilters';
import { IGlobalAdPopUp, IHomepageSections, IHomepageStructure, IKeyBannerProperty } from '@/interfaces/homepage';
import {
  IBackInStockSubscribe,
  IBrandList,
  IProductDetails,
  IProductList,
  IProductListPayload,
  IProductModel,
  ISearchProduct,
  IWishListProducts,
} from '@/interfaces/product';
import { serverSideAxiosInstance } from '@/utils/serverSideAxiosClientWithInterceptor';
import axiosInstanceFromComponent from '../components/AxiosInterceptorComponent';

let axiosInstance = serverSideAxiosInstance;
if (!isServerSide()) {
  axiosInstance = axiosInstanceFromComponent;
}
export const getCategory = async (pageSize: number, pageIndex: number): Promise<ICategory> => {
  let body = {
    includeChildCategory: true,
  };
  const response = await axiosInstance.post(`${APEX_API_URL}/api/catalog/category/list?pageSize=${pageSize}&pageIndex=${pageIndex}`, body);
  return response.data;
};

export const getProducts = async (
  pageSize: number,
  pageIndex: number,
  productListPayload?: IProductListPayload,
  IncludeGiftVouchers: boolean = false,
): Promise<IProductList> => {
  let body = {
    categoryIds: productListPayload?.categoryIds,
    showDefaultImageOnly: productListPayload?.showDefaultImageOnly,
    IncludeGiftVouchers: IncludeGiftVouchers,
  };

  const response = await axiosInstance.post(`${APEX_API_URL}/api/catalog/product/list?pageSize=${pageSize}&pageIndex=${pageIndex}`, body);
  return response.data;
};

export const getMemberExclusive = async (pageSize: number, pageIndex: number, session?: INextAuthSessionModel | null): Promise<IProductList> => {
  const response = await axiosInstance.post(`${APEX_API_URL}/api/catalog/product/member-exclusive?pageSize=${pageSize}&pageIndex=${pageIndex}`, {
    headers: {
      Authorization: 'Bearer ' + session?.accessToken,
    },
  });
  return response.data;
};

export const getProduct = async (
  slug: string,
  returnInStockOnly: boolean = true,
  session?: INextAuthSessionModel | null,
): Promise<IProductDetails> => {
  const response = await axiosInstance.get(`${APEX_API_URL}/api/catalog/product/slug/${slug}`, {
    params: {
      returnInStockOnly,
    },
    headers: {
      Authorization: `Bearer ${session?.accessToken}`,
    },
  });
  return response.data;
};

export const getHomepageStructure = async (): Promise<IHomepageStructure> => {
  let url = `${APEX_API_URL}/api/homepage`;
  const response = await axiosInstance.get(url);
  return response.data;
};
export const getMegaMenu = async (): Promise<IMegaMenu> => {
  let url = `${APEX_API_URL}/api/homepage/mega-menus`;
  const response = await axiosInstance.get(url);
  return response.data;
};
export const getHomepageSections = async (): Promise<IHomepageSections> => {
  let url = `${APEX_API_URL}/api/homepage/body`;
  const response = await axiosInstance.get(url);
  //  throw new Error('custom error');
  return response.data;
};
export const getRecommendedProductList = async (slug: string): Promise<IProductModel[]> => {
  const response = await axiosInstance.post(`${APEX_API_URL}/api/catalog/product/recommended-products/${slug}`);
  return response.data;
};
export const getCrossCategorySuggestionsList = async (slug: string): Promise<IProductModel[]> => {
  const response = await axiosInstance.post(`${APEX_API_URL}/api/catalog/product/cross-category-suggestions/${slug}`);
  return response.data;
};
export const getWishlist = async (slug: string, session?: INextAuthSessionModel | null): Promise<IWishListProducts> => {
  const response = await axiosInstance.get(`${APEX_API_URL}/api/catalog/product/${slug}/wishlist`, {
    headers: {
      Authorization: `Bearer ${session?.accessToken}`,
    },
  });
  return response.data;
};

export const searchProduct = async (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  resolveFilters: boolean = true,
): Promise<ISearchProduct> => {
  let url = `${APEX_API_URL}/api/search?query=${query}&filters=${filters}&pageIndex=${pageIndex}&pageSize=${pageSize}&sorting=${sorting}&resolveFilters=${resolveFilters}`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const searchAutoComplete = async (query: string): Promise<ISearchProduct> => {
  let url = `${APEX_API_URL}/api/search/auto-complete?query=${query}`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const memberExclusiveList = async (
  query: string,
  filters: string,
  pageIndex: number,
  pageSize: number,
  sorting: string,
  session?: INextAuthSessionModel | null,
): Promise<ISearchProduct> => {
  let url = `${APEX_API_URL}/api/search/member-exclusive?query=${query}&filters=${filters}&pageIndex=${pageIndex}&pageSize=${pageSize}&sorting=${sorting}`;
  const response = await axiosInstance.get(url, {
    headers: {
      Authorization: `Bearer ${session?.accessToken}`,
    },
  });
  return response.data;
};

export const getRecentlyViewedProducts = async (arrayOfProductIds: string[]): Promise<IProductModel[]> => {
  let tempQueryParams = '?';
  arrayOfProductIds.forEach((productId, index) => {
    if (index > 0) {
      tempQueryParams += '&';
    }
    tempQueryParams += 'productIds=' + productId;
  });

  let url = `${APEX_API_URL}/api/catalog/product/recently-visited-products${tempQueryParams}`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getPopularSearches = async (): Promise<any> => {
  let url = `${APEX_API_URL}/api/search/trending`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const backInStockSubscribe = async (body: IBackInStockSubscribe): Promise<any> => {
  const response = await axiosInstance.post(`${APEX_API_URL}/api/back-in-stock-notification/subscribe`, body);
  return response.data;
};
export const backInStockSubscribeAnonymous = async (body: IBackInStockSubscribe, captchaResponse?: string): Promise<any> => {
  const response = await axiosInstance.post(`${APEX_API_URL}/api/back-in-stock-notification/anonymous-subscribe`, body, {
    params: {
      captchaResponse: captchaResponse,
    },
  });
  return response.data;
};

export const getCategoryCustomFilter = async (slug: string, includeFeaturedImage: boolean): Promise<ICustomFilter> => {
  let url = `${APEX_API_URL}/api/catalog/category/slug`;
  const response = await axiosInstance.get(url, {
    params: {
      slug,
      includeFeaturedImage,
    },
  });

  return response.data;
};

export const getBrandCustomFilter = async (seName: string): Promise<IBrandCustomFilters> => {
  let url = `${APEX_API_URL}/api/brand/slug/${seName}`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getBrandList = async (): Promise<IBrandList[]> => {
  let url = `${APEX_API_URL}/api/brand/list`;
  const response = await axiosInstance.get(url);
  return response.data;
};
export const getModuleBanner = async (landingBanner: BannerPayLoad): Promise<IKeyBannerProperty[]> => {
  let body = {
    section: landingBanner.type,
    slug: landingBanner.seName,
  };
  let url = `${APEX_API_URL}/api/homepage/landing-banner?section=${body.section}&slug=${body.slug}`;
  const response = await axiosInstance.get(url);
  return response.data;
};
export const getProductInfoGraphic = async (slug: string): Promise<number> => {
  let url = `${APEX_API_URL}/api/catalog/product/product-info-graphic/${slug}`;
  const response = await axiosInstance.get(url);
  return response.data;
};
export const postProductInfoGraphic = async (slug: string): Promise<any> => {
  let url = `${APEX_API_URL}/api/catalog/product/product-info-graphic/${slug}`;
  const response = await axiosInstance.post(url);
  return response.data;
};
export const getGlobalAdPopUp = async (): Promise<IGlobalAdPopUp> => {
  let url = `${APEX_API_URL}/api/GlobalPromotionalPopup`;
  const response = await axiosInstance.get(url);
  return response.data;
};
export const getColumnBanners = async (): Promise<any> => {
  let url = `${APEX_API_URL}/api/banner/column-banners`;
  const response = await axiosInstance.get(url);
  return response.data;
};

export const getStyleSVGSourceCode = async (): Promise<{ [key: string]: string }> => {
  let url = `${APEX_API_URL}/api/catalog/product/product-all-styles-svg`;
  const response = await axiosInstance.get(url);
  return response.data;
};
